<script setup>
/**
 * Conversations per day, and how many of them ended up with a person.
 *
 * ── THE COLOUR IS THE WHOLE POINT OF THIS FILE ────────────────────────────────────────
 * The bars are `fill-n-blue-9`, NEVER `fill-n-brand`. THE PLAN §5 R3 calls this the trap
 * that would have shipped a blue chart in a green product, and it is not a style
 * preference: `theme/colors.js` defines `brand` as a fixed Chatwoot-blue hex, and the
 * branding overlay recolours it emerald by ENUMERATING about forty-five exact utility
 * selectors with `!important`. `fill-n-brand` is not one of them and could not be — you
 * cannot enumerate every utility a chart might need. So it would have fallen straight
 * through to `#2781F6`, in both themes, while passing any lint that only checks for an
 * `n-` prefix. The `n-blue-*` scale is genuinely variable-driven (`--blue-1..12`,
 * re-declared emerald by the overlay in BOTH `:root` and `.dark`), which is exactly why
 * Chatwoot's own Captain charts use it. `waslo-lint.mjs` R3 fails the image build on the
 * other one.
 *
 * ── WHY AN SVG WE DRAW, RATHER THAN A CHART LIBRARY ───────────────────────────────────
 * Every charting library ships its own palette, its own dark-mode strategy and its own
 * font stack — a second theme engine, which is complaint #1 in the plan and the thing this
 * whole section exists to delete. Colour here comes from the same `n-*` tokens as the rest
 * of the page, the type is the page's own Inter, and there is nothing to keep in sync in
 * two themes because there is no second declaration anywhere.
 *
 * ── WHAT THE SHAPE SAYS ───────────────────────────────────────────────────────────────
 * One bar per day, stacked, NOT two bars side by side: a handover is a SUBSET of that
 * day's conversations, and two bars would invite reading them as two independent totals.
 * The handed-over part sits at the TOP of the bar in `n-slate-8` — deliberately not an
 * accent, not amber, not ruby. A handover is not a failure (it is often the correct
 * outcome and sometimes an owner's own rule), so colouring it as a warning would be this
 * page passing a judgement it has no way to make.
 *
 * ── DIRECTION ─────────────────────────────────────────────────────────────────────────
 * The plot is pinned `direction: ltr`, including on an Arabic account. Time runs
 * left-to-right in every dashboard an owner has ever seen, mirroring it would flip the
 * meaning of "the last bar", and `text-anchor="end"` resolves against the inline direction
 * — so leaving it inherited would move the axis labels to the wrong side on exactly the
 * accounts nobody screenshots. The LABELS are still translated and still formatted in the
 * account's locale; only the axis order is fixed.
 */
import { computed } from 'vue';

import CardLayout from 'dashboard/components-next/CardLayout.vue';

import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  /** `[{ day: 'YYYY-MM-DD', conversations, handoffConversations }]`, oldest first. */
  points: { type: Array, required: true },
});

const { t, locale } = useWasloI18n();

const intlLocale = computed(() => toIntlLocale(locale.value));

// ── the plot box, in user units ────────────────────────────────────────────────────
// A fixed viewBox scaled by CSS: the SVG is `w-full h-auto`, so the whole plot stretches
// with the column instead of being re-laid-out on every resize. Type inside it is sized in
// user units, which at the 5xl column width lands at roughly 11-12 CSS px.
const VIEW_W = 720;
const AXIS_W = 40;
const PAD_TOP = 10;
const PAD_END = 6;
const PLOT_H = 176;
const LABEL_H = 26;
const VIEW_H = PAD_TOP + PLOT_H + LABEL_H;
const PLOT_X = AXIS_W;
const PLOT_W = VIEW_W - AXIS_W - PAD_END;
const BASELINE = PAD_TOP + PLOT_H;
/** So one lone conversation is a visible mark rather than a rounding error. */
const MIN_BAR = 2;

const viewBox = `0 0 ${VIEW_W} ${VIEW_H}`;

/**
 * A top gridline an owner can read. `Math.max(...)` alone puts the tallest bar flush
 * against the frame and labels the axis `17`, which looks like a measurement rather than a
 * scale; rounding to a round-ish number keeps the bars off the ceiling and makes the
 * middle gridline mean something.
 */
const niceMax = value => {
  if (value <= 4) return Math.max(1, Math.ceil(value));
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const step = steps.find(candidate => value <= candidate * magnitude) ?? 10;
  return Math.round(step * magnitude);
};

const busiest = computed(() =>
  props.points.reduce((max, point) => Math.max(max, point.conversations), 0)
);

/** Nothing at all happened. The card says so in words instead of drawing a flat line. */
const isEmpty = computed(() => busiest.value === 0);

const scaleMax = computed(() => niceMax(busiest.value));

const formatCount = value =>
  new Intl.NumberFormat(intlLocale.value).format(value);

// `YYYY-MM-DD` is a UTC day (the rollup's, not the browser's). Parsing it as an instant
// and formatting it back in UTC is what stops a Kuwait browser labelling the 10th as the
// 9th for the first three hours of every day.
const dayDate = day => new Date(`${day}T00:00:00.000Z`);

const shortDay = computed(() => {
  const format = new Intl.DateTimeFormat(intlLocale.value, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return day => format.format(dayDate(day));
});

const longDay = computed(() => {
  const format = new Intl.DateTimeFormat(intlLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  return day => format.format(dayDate(day));
});

/**
 * At most six dates on the axis, and the LAST day is always one of them — it is the day
 * the owner is actually asking about, and an axis that ends three days before the data
 * does reads as a chart that stopped updating. Ninety labels at 7px apart is not a denser
 * axis, it is an unreadable one.
 */
const MAX_TICKS = 6;

const bars = computed(() => {
  const points = props.points;
  const count = points.length;
  if (count === 0) return [];

  const step = PLOT_W / count;
  const width = Math.max(2, Math.min(step * 0.62, 28));
  const tickEvery = Math.max(1, Math.ceil(count / MAX_TICKS));
  const max = scaleMax.value;

  return points.map((point, index) => {
    const height =
      point.conversations > 0
        ? Math.max(MIN_BAR, (point.conversations / max) * PLOT_H)
        : 0;
    const handed =
      point.handoffConversations > 0
        ? Math.min(
            height,
            Math.max(MIN_BAR, (point.handoffConversations / max) * PLOT_H)
          )
        : 0;
    const centre = PLOT_X + step * index + step / 2;

    return {
      key: point.day,
      // The column's own hit area: a full-height transparent rect, so the tooltip is
      // reachable on a day with one conversation and a 2px bar.
      columnX: PLOT_X + step * index,
      columnW: step,
      x: centre - width / 2,
      w: width,
      y: BASELINE - height,
      h: height,
      handedY: BASELINE - height,
      handedH: handed,
      label: shortDay.value(point.day),
      labelX: centre,
      // Counted back from the END so the last day always carries a label.
      showLabel: (count - 1 - index) % tickEvery === 0,
      title: t('WASLO.ACTIVITY.TREND.BAR', {
        day: longDay.value(point.day),
        conversations: formatCount(point.conversations),
        handoffs: formatCount(point.handoffConversations),
      }),
    };
  });
});

/**
 * Zero, half, top. Only the two ends are labelled; the middle one is a reading aid.
 *
 * NOT `n-weak`, which is what these were first drawn in and which is the trap this whole
 * contract exists to catch — measured against the card it sits on, `--border-weak` is 21
 * levels of separation in light and TWO in dark, so the light chart had gridlines and the
 * dark one silently had none. `n-slate-5` measures 31 and 17: a hairline in both. The
 * baseline is a step stronger because it is the line the bars stand on, not a reading aid.
 */
const gridLines = computed(() =>
  [0, 0.5, 1].map(fraction => ({
    key: String(fraction),
    y: BASELINE - fraction * PLOT_H,
    stroke: fraction === 0 ? 'stroke-n-slate-7' : 'stroke-n-slate-5',
    label:
      fraction === 0.5
        ? ''
        : formatCount(Math.round(fraction * scaleMax.value)),
  }))
);

const ariaLabel = computed(() =>
  t('WASLO.ACTIVITY.TREND.ARIA', { days: props.points.length })
);
</script>

<template>
  <CardLayout>
    <!-- The legend is HTML, not SVG: it is prose with two swatches, and it has to wrap
         with the column on a narrow screen. -->
    <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
      <span class="inline-flex items-center gap-2 text-sm text-n-slate-11">
        <span class="rounded-sm size-2.5 bg-n-blue-9" aria-hidden="true" />
        {{ t('WASLO.ACTIVITY.TREND.ANSWERED') }}
      </span>
      <span class="inline-flex items-center gap-2 text-sm text-n-slate-11">
        <span class="rounded-sm size-2.5 bg-n-slate-8" aria-hidden="true" />
        {{ t('WASLO.ACTIVITY.TREND.HANDED') }}
      </span>
    </div>

    <p v-if="isEmpty" class="py-6 mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.ACTIVITY.TREND.NONE') }}
    </p>

    <svg
      v-else
      :viewBox="viewBox"
      class="w-full h-auto"
      direction="ltr"
      role="img"
      :aria-label="ariaLabel"
      data-waslo-trend-chart
    >
      <g>
        <line
          v-for="line in gridLines"
          :key="`grid-${line.key}`"
          :x1="PLOT_X"
          :x2="VIEW_W - PAD_END"
          :y1="line.y"
          :y2="line.y"
          :class="line.stroke"
          stroke-width="1"
        />
        <text
          v-for="line in gridLines"
          :key="`ylabel-${line.key}`"
          :x="AXIS_W - 10"
          :y="line.y + 4"
          text-anchor="end"
          class="text-[11px] fill-n-slate-10"
        >
          {{ line.label }}
        </text>
      </g>

      <g v-for="bar in bars" :key="bar.key">
        <!-- Answered by the assistant: the accent, remapped emerald in both themes. -->
        <rect
          v-if="bar.h > 0"
          :x="bar.x"
          :y="bar.y"
          :width="bar.w"
          :height="bar.h"
          rx="2"
          class="fill-n-blue-9"
        />
        <!-- The part that reached a person, drawn on top of it — a subset, not a rival. -->
        <rect
          v-if="bar.handedH > 0"
          :x="bar.x"
          :y="bar.handedY"
          :width="bar.w"
          :height="bar.handedH"
          rx="2"
          class="fill-n-slate-8"
        />
        <text
          v-if="bar.showLabel"
          :x="bar.labelX"
          :y="BASELINE + 18"
          text-anchor="middle"
          class="text-[11px] fill-n-slate-10"
        >
          {{ bar.label }}
        </text>
        <!-- Hit area last, so it sits above the bars. `fill-transparent` still takes
             pointer events; `fill="none"` would not. -->
        <rect
          :x="bar.columnX"
          :y="PAD_TOP"
          :width="bar.columnW"
          :height="PLOT_H"
          class="fill-transparent"
        >
          <title>{{ bar.title }}</title>
        </rect>
      </g>
    </svg>
  </CardLayout>
</template>
